import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Clip } from '@airhead-games/shared/types/configs/media/clipLibrary';

interface UseClipBatchingOptions {
  clips: Clip[];
  batchSize?: number;
  initialEagerCount?: number;
  onLoadMore?: (nextVisibleCount: number, totalCount: number) => void;
}

interface UseClipBatchingResult {
  eagerCount: number;
  hasMore: boolean;
  mountedClips: Clip[];
  isClipVisible: (index: number) => boolean;
  handleClipLoad: (clipUrl: string) => void;
  handleLoadMore: () => void;
}

export function useClipBatching({
  clips,
  batchSize,
  initialEagerCount = 3,
  onLoadMore,
}: UseClipBatchingOptions): UseClipBatchingResult {
  const paginated = typeof batchSize === 'number' && batchSize > 0;
  const initialVisibleCount = paginated
    ? Math.min(batchSize, clips.length)
    : clips.length;
  const initialPreloadCount = paginated ? initialVisibleCount : clips.length;

  const [visibleCount, setVisibleCount] = useState(initialVisibleCount);
  const [preloadCount, setPreloadCount] = useState(initialPreloadCount);
  const [loadedClipUrls, setLoadedClipUrls] = useState<Set<string>>(
    () => new Set()
  );

  useEffect(() => {
    setVisibleCount(initialVisibleCount);
    setPreloadCount(initialPreloadCount);
    setLoadedClipUrls(new Set());
  }, [clips, initialPreloadCount, initialVisibleCount]);

  const initialVisibleClips = useMemo(
    () => (paginated ? clips.slice(0, initialVisibleCount) : clips),
    [clips, initialVisibleCount, paginated]
  );
  const mountedClips = paginated ? clips.slice(0, preloadCount) : clips;
  const hasMore = paginated && visibleCount < clips.length;
  const eagerCount = paginated ? preloadCount : initialEagerCount;
  const initialBatchLoaded =
    initialVisibleClips.length > 0 &&
    initialVisibleClips.every((clip) => loadedClipUrls.has(clip.clipUrl));

  const handleClipLoad = useCallback((clipUrl: string) => {
    setLoadedClipUrls((currentLoadedClipUrls) => {
      if (currentLoadedClipUrls.has(clipUrl)) {
        return currentLoadedClipUrls;
      }

      const nextLoadedClipUrls = new Set(currentLoadedClipUrls);
      nextLoadedClipUrls.add(clipUrl);
      return nextLoadedClipUrls;
    });
  }, []);

  useEffect(() => {
    if (
      !paginated ||
      !batchSize ||
      preloadCount > visibleCount ||
      !initialBatchLoaded
    ) {
      return;
    }

    const nextPreloadCount = Math.min(visibleCount + batchSize, clips.length);
    if (nextPreloadCount > preloadCount) {
      setPreloadCount(nextPreloadCount);
    }
  }, [
    batchSize,
    clips.length,
    initialBatchLoaded,
    paginated,
    preloadCount,
    visibleCount,
  ]);

  const handleLoadMore = useCallback(() => {
    if (!paginated || !batchSize) {
      return;
    }

    const nextVisibleCount = Math.min(visibleCount + batchSize, clips.length);
    const nextPreloadCount = Math.min(
      nextVisibleCount + batchSize,
      clips.length
    );

    setVisibleCount(nextVisibleCount);
    setPreloadCount(nextPreloadCount);
    onLoadMore?.(nextVisibleCount, clips.length);
  }, [batchSize, clips.length, onLoadMore, paginated, visibleCount]);

  const isClipVisible = useCallback(
    (index: number) => !paginated || index < visibleCount,
    [paginated, visibleCount]
  );

  return {
    eagerCount,
    hasMore,
    mountedClips,
    isClipVisible,
    handleClipLoad,
    handleLoadMore,
  };
}
